import { useEffect, useState } from 'react';
import { zxcvbnOptions } from '@zxcvbn-ts/core';
import { ConfigService } from '@nextgen-web-framework/core';

/**
 * Custom hook that fetches zxcvbn dictionaries using fetch.
 *
 * Usage: Call this hook at a top-level component so that
 * the zxcvbn options are set before any password strength calculations occur.
 *
 * Returns `true` once the full options have been applied.
 */
export function usePasswordDictionaries(): boolean {
  const { NEXT_ASSETS_BASE_PATH } = ConfigService.getConfigs();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    async function fetchDictionaries() {
      const passwordsUrl = `${NEXT_ASSETS_BASE_PATH}/password-strength/passwords.json`;
      const adjacencyGraphsUrl = `${NEXT_ASSETS_BASE_PATH}/password-strength/adjacencyGraphs.json`;
      const dicewareUrl = `${NEXT_ASSETS_BASE_PATH}/password-strength/diceware.json`;

      try {
        const [passwordsResponse, graphsResponse, dicewareResponse] = await Promise.all([
          fetch(passwordsUrl),
          fetch(adjacencyGraphsUrl),
          fetch(dicewareUrl),
        ]);

        if (!passwordsResponse.ok || !graphsResponse.ok || !dicewareResponse.ok) {
          // eslint-disable-next-line no-console
          console.warn('One or more dictionary files failed to load.');
          return;
        }

        const passwords = await passwordsResponse.json();
        const adjacencyGraphs = await graphsResponse.json();
        const diceware = await dicewareResponse.json();

        zxcvbnOptions.setOptions({
          dictionary: {
            passwords: passwords,
            diceware: diceware,
          },
          useLevenshteinDistance: true,
          graphs: adjacencyGraphs,
        });

        // signal that future zxcvbn calls will use the full data
        setLoaded(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load zxcvbn dictionaries via fetch', err);
      }
    }

    fetchDictionaries();
  }, [NEXT_ASSETS_BASE_PATH]);

  return loaded;
}
